import React, { useEffect, useState, useCallback } from 'react';
import { Button, TextField, Typography, InputAdornment, IconButton } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ErrorDialog from './ErrorDialog';
import SuccessDialog from './SuccessDialog';
import LoadingDialog from './LoadingDialog';
import { Messages } from '../constants/Messages';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { FILE_LINK, SHEET_NAME, FONT_COLOR_RED, DOWNLOAD_FILE_NAME } from '../constants/Constants';
import { Switch } from "../controls/Switch";
interface Paragraph {
    text: string;
    id: number;
    source: string;
}

interface Keyword {
    id: number;
    keyword: string;
    count: number;
    clause: string;
    isExcluded?: boolean;
}

interface DownloadExcelProps {
    files: FileList | null;
    keywords: Keyword[];
    excludedKeywords: Keyword[];
    mergeStatus: string;
    setMergeStatus: (status: string) => void;
    xmlContent: string | null;
}

const escapeRegExp = (string: string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const DownloadExcel: React.FC<DownloadExcelProps> = ({
    files,
    keywords,
    excludedKeywords,
    mergeStatus,
    setMergeStatus,
    xmlContent
}) => {
    const [paragraphs, setParagraphs] = useState<Paragraph[]>([]);
    const [filterKeyword, setFilterKeyword] = useState('');
    const [debouncedFilterKeyword, setDebouncedFilterKeyword] = useState('');
    const [filteredKeywordsCount, setFilteredKeywordsCount] = useState(0);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [tableRows, setTableRows] = useState(0);
    const [rows, setRows] = useState<JSX.Element[]>([]);
    const [tableData, setTableData] = useState<{ keyword: string; text: string; source: string }[]>([]);
    const [isComposing, setIsComposing] = useState(false);
    const [splitByArticle, setSplitByArticle] = useState(false);

    const handleCloseErrorDialog = useCallback(() => {
        setErrorDialogOpen(false);
    }, []);

    const handleCloseSuccessDialog = useCallback(() => {
        setSuccessDialogOpen(false);
    }, []);

    const parseXmlContent = useCallback((xmlContent: string): Paragraph[] => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlContent, "text/xml");
        return Array.from(xmlDoc.getElementsByTagName('w:p')).map((paragraph, idx) => {
            const paragraphText = Array.from(paragraph.getElementsByTagName('w:t')).map(node => node.textContent).join('');
            let parent: Element | null = paragraph.parentElement;
            let source = '';
            while (parent) {
                if (parent.tagName === 'w:document' && parent.hasAttribute('source')) {
                    const attr = parent.getAttribute('source');
                    if (attr !== null) {
                        source = attr;
                        break;
                    }
                }
                parent = parent.parentElement;
            }
            return { text: paragraphText, id: idx, source: source };
        });
    }, []);

    const handleDelete = useCallback((paragraphId: number, keyword: string) => {
        setParagraphs(prevParagraphs => prevParagraphs.map(paragraph => {
            if (paragraph.id === paragraphId) {
                return {
                    ...paragraph,
                    text: paragraph.text.replace(new RegExp(escapeRegExp(keyword), 'gi'), '')
                };
            }
            return paragraph;
        }).filter(paragraph => paragraph.text.trim().length > 0));
    }, []);

    useEffect(() => {
        if (mergeStatus === 'completed' && xmlContent) {
            const parsedParagraphs = parseXmlContent(xmlContent);

            // 条文単位に区切る
            if (splitByArticle) {
                const newParagraph: Paragraph[] = [];
                const regexes: RegExp[] = [
                    /第[0-9０-９一二三四五六七八九十百千]+条[ \u3000]/,
                    /第[0-9０-９一二三四五六七八九十百千]+条の[0-9０-９一二三四五六七八九十百千]+[ \u3000]/,
                    /第[0-9０-９一二三四五六七八九十百千]+条の[0-9０-９一二三四五六七八九十百千]+の[0-9０-９一二三四五六七八九十百千]+[ \u3000]/,
                    /第[0-9０-９一二三四五六七八九十百千]+[ \u3000]/,
                    /^付則/,
                    /^付[ \u3000]+則/,
                    /^附則/,
                    /^附[ \u3000]+則/,
                    /^別表/
                ];
                let article = "";
                let detectDem = false;
                let bfId = 0;
                let bfText = "";
                let bfSource = "";

                // paragraphを条単位に整形
                parsedParagraphs.forEach(paragraphObj => {
                    const id = paragraphObj.id;
                    const text = paragraphObj.text;
                    const source = paragraphObj.source;
                    
                    if (source !== bfSource) {
                        article = "";
                        bfText = "";
                        detectDem = false;
                    }

                    // 区切り位置を検索
                    if (regexes.some(regex => regex.test(text))) {
                        
                        if (detectDem && article.length > 0) { // 最初の区切り位置までは対象としない
                            // 前行が見出しの場合は最終行を削除
                            if (/^[(（].*[)）]$/.test(bfText)) article = article.split('\n').slice(0, -1).join('\n');

                            newParagraph.push({id: bfId ,text: article, source: bfSource }); // 条文をセット
                        }

                        article = ""; // 条文を初期化
                        // 前行が見出しの場合、先頭に見出しをセット
                        if (/^[(（].*[)）]$/.test(bfText)) article = bfText
                        detectDem = true;
                    }

                    if (article.length > 0) article += "\n";
                    article += text;
                    bfId = id;
                    bfText = text;
                    bfSource = source;
                });
                if (detectDem && article.length > 0) { // 最初の区切り位置までは対象としない
                    newParagraph.push({id: bfId ,text: article, source: bfSource }); // 条文をセット
                } 

                setParagraphs(newParagraph);
            }else {
                setParagraphs(parsedParagraphs);
            }

        } else {
            setParagraphs([]);
        }
    }, [mergeStatus, xmlContent, parseXmlContent, splitByArticle]);

    useEffect(() => {
        setParagraphs([]);
        setIsButtonDisabled(true);
        if (files) {
            setMergeStatus('idle');
        }
    }, [files, setMergeStatus]);

    useEffect(() => {
        if (mergeStatus === 'completed' && tableRows > 0) {
            setIsButtonDisabled(false);
        } else {
            setIsButtonDisabled(true);
        }
    }, [mergeStatus, tableRows]);

    const handleFilterSubmit = useCallback(() => {
        setDebouncedFilterKeyword(filterKeyword);
    }, [filterKeyword]);

    const createHighlightSpans = (text: string, keyword: string, excludedKeywords: string[]) => {
        const excludedRegex = new RegExp(`(${excludedKeywords.map(escapeRegExp).join('|')})`, 'gi');
        const keywordRegex = new RegExp(`(${escapeRegExp(keyword)})`, 'gi');
        let textParts: { text: string; isExcluded: boolean; isKeyword?: boolean }[] = text.split(excludedRegex).map((part, index) => ({
            text: part,
            isExcluded: index % 2 !== 0,
        }));

        textParts = textParts.flatMap(part => {
            if (part.isExcluded) {
                return [part];
            }
            return part.text.split(keywordRegex).map((subPart, index) => ({
                text: subPart,
                isKeyword: index % 2 !== 0,
                isExcluded: false,
            }));
        });

        return textParts.map((part, index) => {
            if (part.isExcluded) {
                return <span key={`excluded-${index}`} style={{ color: 'black', fontWeight: 'normal' }}>{part.text}</span>;
            }
            if (part.isKeyword) {
                return <span key={`keyword-${index}`} style={{ color: 'red', fontWeight: 'bold' }}>{part.text}</span>;
            }
            return part.text;
        });
    };

    const removeExcludedKeywords = useCallback((text: string, excludedKeywords: string[]) => {
        let modifiedText = text;
        excludedKeywords.forEach(excludedKeyword => {
            const escapedKeyword = escapeRegExp(excludedKeyword);
            const regex = new RegExp(escapedKeyword, 'g');
            modifiedText = modifiedText.replace(regex, '');
        });
        return modifiedText;
    }, []);

    useEffect(() => {
        let rowNumber = 0;
        const newRows: JSX.Element[] = [];
        const newTableData: { keyword: string; text: string; source: string }[] = [];
        let totalKeywordHits = 0;

        paragraphs.forEach(paragraphObj => {
            const paragraph = paragraphObj.text;
            const source = paragraphObj.source;
            
            // 検索キーワードがパラグラフに含まれているか確認
            const matchingKeywords = keywords.filter(keyword =>
                paragraph.includes(keyword.keyword) &&
                (!debouncedFilterKeyword || keyword.keyword.includes(debouncedFilterKeyword))
            ).map(k => k.keyword);

            // パラグラフに検索キーワードが含まれている場合のみ処理
            if (matchingKeywords.length > 0) {
                // パラグラフに除外キーワードが含まれているか確認
                //const containsExcludedKeyword = excludedKeywords.some(excludedKeyword =>
                excludedKeywords.some(excludedKeyword =>
                    paragraph.includes(excludedKeyword.keyword)
                );

                // 除外キーワードを削除
                const cleanedParagraph = removeExcludedKeywords(paragraph, excludedKeywords.map(k => k.keyword));

                // 再度、検索キーワードが含まれているか確認
                const cleanedMatchingKeywords = keywords.filter(keyword =>
                    cleanedParagraph.includes(keyword.keyword) &&
                    (!debouncedFilterKeyword || keyword.keyword.includes(debouncedFilterKeyword))
                ).map(k => k.keyword);

                // ファイル名の拡張子を削除
                const sourceWithoutExt = source.slice(0, source.lastIndexOf('.')) || source;

                if (cleanedMatchingKeywords.length > 0) {
                    cleanedMatchingKeywords.forEach(keyword => {
                        const combinedText = createHighlightSpans(paragraph, keyword, excludedKeywords.map(k => k.keyword));
                        const keywordHits = (cleanedParagraph.match(new RegExp(`(${escapeRegExp(keyword)})`, 'gi')) || []).length;
                        totalKeywordHits += keywordHits;

                        rowNumber++;
                        newRows.push(
                            <tr key={`keyword-${paragraphObj.id}-${rowNumber}`}>
                                <td>{rowNumber}</td>
                                <td>{sourceWithoutExt}</td>
                                <td>{keyword}</td>
                                <td>{combinedText}</td>
                                <td>
                                    <Button onClick={() => handleDelete(paragraphObj.id, keyword)} variant='outlined'>削除</Button>
                                </td>
                            </tr>
                        );

                        newTableData.push({ keyword, text: combinedText.map(part => typeof part === 'string' ? part : part.props.children).join(''), source: sourceWithoutExt });
                    });
                }
            }
        });

        setRows(newRows);
        setTableRows(newRows.length);
        setFilteredKeywordsCount(totalKeywordHits);
        setTableData(newTableData);
    }, [debouncedFilterKeyword, keywords, paragraphs, excludedKeywords, handleDelete, removeExcludedKeywords]);

    const createRichTextParts = useCallback((text: string, keyword: string): ExcelJS.RichText[] => {
        return text.split(new RegExp(`(${escapeRegExp(keyword)})`, 'gi')).map(part =>
            part.toLowerCase() === keyword.toLowerCase() ?
                { text: part, font: { color: { argb: FONT_COLOR_RED }, bold: true } } :
                { text: part } // bug fix(2025/10/13)
        );
    }, []);

    const downloadExcel = async () => {
        if (mergeStatus !== 'completed' || !xmlContent) return;

        setIsLoading(true);
        try {
            const url = window.location.href;
            let baseUrl = url.replace(/index\.html$/, '');
            while (baseUrl.endsWith('/')) {
                baseUrl = baseUrl.slice(0, -1)
            }
            const fileUrl = baseUrl + FILE_LINK;
            const workbook = new ExcelJS.Workbook();
            const response = await fetch(fileUrl);
            const arrayBuffer = await response.arrayBuffer();
            await workbook.xlsx.load(arrayBuffer);

            const sheet = workbook.getWorksheet(SHEET_NAME);
            if (!sheet) {
                setErrorMessage(Messages.EXCEL_SHEET_ERROR);
                setErrorDialogOpen(true);
                return;
            }

            // 12行目を基準にデータ行を作成
            for (let i = 0; i < tableData.length - 1; i++) {
                const sourceRow = sheet.getRow(12);
                const targetRow = sheet.insertRow(13, sourceRow.values);
                sourceRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                    const targetCell = targetRow.getCell(colNumber);
                    targetCell.style = { ...cell.style };
                    if (cell.dataValidation) {
                        targetCell.dataValidation = { ...cell.dataValidation };
                    }
                });
            }
            // データのセット
            let rowIndex = 12;
            tableData.forEach(data => {
                const { keyword, text, source } = data;

                const cellRefIndex = sheet.getCell(`A${rowIndex}`);
                cellRefIndex.value = rowIndex - 11;
                cellRefIndex.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true  };

                const cellRefFile = sheet.getCell(`B${rowIndex}`);
                cellRefFile.value = source;
                cellRefFile.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true  };

                const cellRefKeyword = sheet.getCell(`F${rowIndex}`);
                cellRefKeyword.value = keyword;
                cellRefKeyword.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true  };

                const richTextParts = createRichTextParts(text, keyword);
                const cellRefText = sheet.getCell(`G${rowIndex}`);
                cellRefText.value = { richText: richTextParts };
                cellRefText.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

                rowIndex++;
            });

            // セレクトボックスを再設定
            rowIndex = 6;
            for (let i = 0; i < tableData.length + 6; i++) {

                const targetCols = ['H', 'U', 'W', 'P', 'Q'];
                const phaseCols = ['I', 'J', 'K', 'L', 'M', 'N', 'O'];
                for (const col of targetCols) {
                    sheet.getCell(`${col}${rowIndex}`).dataValidation = {
                        type: 'list',
                        allowBlank: true,
                        formulae: ['Sheet2!$B$4:$B$5'],
                        showErrorMessage: true
                    };
                }
                for (const col of phaseCols) {
                    sheet.getCell(`${col}${rowIndex}`).dataValidation = {
                        type: 'list',
                        allowBlank: true,
                        formulae: ['=Sheet2!$C$4:$C$6'],
                        showErrorMessage: true
                    };
                }
                rowIndex++;
            }

            // 印刷範囲をクリア
            sheet.pageSetup.printArea = undefined;

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            saveAs(blob, DOWNLOAD_FILE_NAME);

            setSuccessDialogOpen(true);
        } catch (error) {
            setErrorMessage(Messages.EXCEL_GENERATION_ERROR);
            setErrorDialogOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const renderKeywordTable = () => (
        <div className='custom-table-container'>
            <div className='custom-filter-container'>
                <TextField
                    label="キーワードで絞り込み"
                    variant="outlined"
                    margin="normal"
                    value={filterKeyword}
                    onChange={(e) => setFilterKeyword(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !isComposing) {
                            handleFilterSubmit();
                        }
                    }}
                    onCompositionStart={() => setIsComposing(true)}
                    onCompositionEnd={() => setIsComposing(false)}
                    className="filter-textfield"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={handleFilterSubmit}>
                                    <SearchIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <div style = {{ marginRight : "20px"}}>
                    <Typography variant="body1">
                        絞り込み結果: {filteredKeywordsCount}件
                    </Typography>
                </div>
                
                <div style = {{ marginLeft : "auto"}}>
                    <Switch
                        isOn={splitByArticle}
                        onToggle={setSplitByArticle}
                        label="条単位に区切る"
                    />
                </div>
            </div>
            <div className='custom-table-wrapper'>
                <table className='caluse-table'>
                    <thead>
                        <tr>
                            <th className='row-number-col'>No.</th>
                            <th className='highlight-file-col'>ファイル</th>
                            <th className='highlight-keyword-col'>キーワード</th>
                            <th className='highlight-caluse-col'>該当条文</th>
                            <th className='action-col'>削除</th>
                        </tr>
                    </thead>
                    {mergeStatus === 'completed' && (
                        <tbody>
                            {rows}
                        </tbody>
                    )}
                </table>
            </div>
        </div>
    );

    const renderAccordion = () => (
        <div className='step-accordion'>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <h3>STEP4: 検索キーワードを含む条文を確認してください。</h3>
                </AccordionSummary>
                <AccordionDetails>
                    {renderKeywordTable()}
                    <div className='button-wrapper'>
                        <Button
                            className='download-button'
                            onClick={downloadExcel}
                            variant='contained'
                            disabled={isButtonDisabled}
                        >
                            点検結果報告書をダウンロード
                        </Button>
                    </div>
                </AccordionDetails>
            </Accordion>
        </div>
    );

    return (
        <>
            <ErrorDialog open={errorDialogOpen} errorMessage={errorMessage} onClose={handleCloseErrorDialog} />
            <SuccessDialog open={successDialogOpen} onClose={handleCloseSuccessDialog} message={Messages.DL_EXCEL} />
            <LoadingDialog open={isLoading} message="" />
            {renderAccordion()}
        </>
    );
};

export default DownloadExcel;
